import type { SessionSource } from "@constants/tracking";
import { getTagSkill } from "@features/session-config/helpers/filter";
import { useSession } from "@features/session/hooks/useSession";
import { requestRecordingPermission } from "@memorang/helpers/device";
import { useToast } from "@memorang/ui/components/Toast";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { RecommendedSkillTask } from "../recommendations/adapters/dashboard-task-types";
import { capLaunchDistribution } from "../recommendations/adapters/launch-distribution";

export const useAdaptiveSessionStart = () => {
	const { t } = useTranslation();
	const { showToast } = useToast();
	const {
		handleCreateCommonSession,
		isCreatingSession,
		showErrorDialog,
		toggleShowErrorDialog,
	} = useSession();
	const [showMicPermissionDialog, setShowMicPermissionDialog] = useState(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: only react to showErrorDialog flipping true
	useEffect(() => {
		if (showErrorDialog) {
			showToast({ message: t("errors.sessionCreation.body") });
			toggleShowErrorDialog();
		}
	}, [showErrorDialog]);

	const startAdaptiveSession = async (
		task: RecommendedSkillTask,
		source: SessionSource = "recommended_tasks",
	) => {
		const { quickStudyMeta } = task;
		if (!quickStudyMeta) {
			return;
		}

		if (getTagSkill(task.recommendation.skill) === "SPEAKING") {
			const granted = await requestRecordingPermission();
			if (!granted) {
				setShowMicPermissionDialog(true);
				return;
			}
		}

		handleCreateCommonSession({
			dist: capLaunchDistribution(
				quickStudyMeta.distribution,
				quickStudyMeta.numItems,
			),
			tags: quickStudyMeta.tags,
			source,
		});
	};

	return {
		startAdaptiveSession,
		isCreatingAdaptiveSession: isCreatingSession,
		showMicPermissionDialog,
		setShowMicPermissionDialog,
	};
};
